import api from '../services/axios';
import { useEffect } from 'react';
import { BaseResponse, isValidBaseResponse } from '../types/api';
import { toaster } from '../components/chakra-ui/toaster';
import { useModalContext } from '../providers/modal-context';
import { ModalActions } from '../enums/api';
import { useNavigate } from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';

/*
 * Global Axios interceptor hook.
 * It catches all API responses, success and error response, it handles:
 * - UI feedback (modals/toasts) if the response matches the BaseResponse structure
 * - Redirections if a `redirect` key is present, even if the response is not a BaseResponse
 */
const useAxiosInterceptor = () => {
  const navigate = useNavigate();
  const { setIsOpen, setModalContext } = useModalContext();
  const {unauthorized: handleUnautorized} = useAuth();

  // Show a toast notification using fields from a BaseResponse
  const setToast = (data: BaseResponse) => {
    // Dismiss any existing toasts before showing a new one
    toaster.dismiss();
    // Set a small delay to ensure the previous toast is removed before showing a new one
    setTimeout(() => {
      toaster.create({
        description: data?.display_message,
        duration: data?.display_duration_seconds ? data.display_duration_seconds * 1000 : 5000,
        type: data.display_level,
        action: {
          label: 'Close',
          onClick: () => {}
        }
      });
    }
    , 200); // 200ms delay
  };

  // Show a modal using fields from a BaseResponse
  const setModal = (data: BaseResponse) => {
    setModalContext((state) => ({
      ...state,
      title: data.display_title || '',
      displayMessage: data.display_message || '',
      btnAction: data.display_button_action || ModalActions.SEND_VERIFY_MAIL,
      buttonText: data.display_button_text || '',
      type: data.display_level
    }));
    setIsOpen(true);
  };

  // Determine whether to show toast or modal based on display_element field
  const displayResponseElement = (responseData: BaseResponse) => {
    // Check if responseData is valid and contains the expected fields
    if (!responseData) return; // Early exit if no response data
    if (!isValidBaseResponse(responseData)) return; // skip if it's not the shape we expect


    if (responseData.display_element === 'alert') {
      setToast(responseData);
    }
    if (responseData.display_element === 'modal') {
      setModal(responseData);
    }
  };
  
  // Effect to set up the Axios response interceptor, set up once when the component mounts
  useEffect(() => {
    // Create Axios response interceptor
    const interceptor = api.interceptors.response.use(
      (response) => {
        // Redirect if exists in response
        // This will always redirect if a redirect key is present, regardless of BaseResponse shape
        if (response?.data?.redirect) {
          navigate(response.data.redirect);
        }

        // Display message if response follows BaseResponse, do it after redirect
        displayResponseElement(response?.data as BaseResponse);

        return response;
      },
      (error) => {
        // *** Error handlers for specific status codes ***
        
        // Firstly redirect if present in error response
        if (error.response?.data?.redirect) {
          navigate(error.response.data.redirect);
        }

        // 401 Unauthorized: Handle Unautorized user remove token, clear user, and show UI msg (modal/alert) (only if BaseResponse type)
        if (error.response?.status === 401) {
          // Check if redirect is present in the response
          let redirectToLogin: boolean = true;  // Default to redirecting to login if not specified
          // If redirect exists in response, navigation will be handled by the interceptor below
          if (error.response?.data?.redirect) {
            redirectToLogin = false; // Do not redirect to login if a redirect is present in the response
          }
          handleUnautorized(redirectToLogin);
          if (error.response?.data && isValidBaseResponse(error.response.data)) {
            // Show UI message if the response is a BaseResponse
            displayResponseElement(error.response.data as BaseResponse);
          } else {
            // Build a custom toast message for 401 responses (if response not recived from server)
            const customMessage = {
              display_message: 'Please login to continue.',
              display_level: 'info',
              display_element: 'alert'
            };
            displayResponseElement(customMessage as BaseResponse);
          }
        }

        // 400 Bad Request: show UI msg (only if BaseResponse type) after redirect
        if (error.response?.status === 400) {
          displayResponseElement(error.response?.data as BaseResponse);
        }

        // 404 Not Found: show UI msg (only if BaseResponse type) after redirect
        if (error.response?.status === 404) {
          displayResponseElement(error.response?.data as BaseResponse);
        }

        // 500 Internal Server Error: show UI msg (only if BaseResponse type) after redirect
        if (error.response?.status === 500) {
          displayResponseElement(error.response?.data);
        }

        // 429 Too Many Requests: show UI msg (only if BaseResponse type) after redirect
        if (error.response?.status === 429) {
          // Try to display the error message from the response if it exists
          if(error.response?.data && isValidBaseResponse(error.response.data)) {
            displayResponseElement(error.response?.data);
          }
          else{
            // Else, Build a custom error message for 429 status
            const customErrorMessage = {
              display_title: 'Too Many Requests',
              display_message: 'Too Many Requests. Please try again later.',
              display_level: 'error',
              display_element: 'alert'
            };
            displayResponseElement(customErrorMessage as BaseResponse);
          }
        }

        // 409 Conflict: show UI msg (only if BaseResponse type) after redirect
        if (error.response?.status === 409) {
          displayResponseElement(error.response?.data as BaseResponse);
        }

        // Propagate the error (let the calling code handle the error)
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on component unmount to avoid leaks or duplicates (e.g. if the hook is used in multiple components)
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);
};

export default useAxiosInterceptor;
